/**
 * @contract
 *   Назначение:  Менеджер отзывов для кабинета ЧОП
 *                Список отзывов с модерацией и возможностью ответа владельца
 *   Компонент:  ReviewManager (client-side)
 *   KEYWORDS:   Reviews, Moderation, OwnerReply, Reputation
 *   LINKS:      USES_API(chop_reviews)
 *               USES_API(chop_reviews.owner_reply)
 */

"use client";

import React, { useState } from "react";
import {
  Star,
  MessageSquare,
  User,
  Briefcase,
  ThumbsUp,
  Reply,
  CheckCircle,
  Clock,
  XCircle,
  Send,
  Loader2,
} from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import { Textarea } from "@oxpa/shared/ui/textarea";
import { Badge } from "@oxpa/shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@oxpa/shared/ui/card";
import { useToast } from "@oxpa/shared/hooks/use-toast";
import { cn } from "@oxpa/shared/lib/utils";

// # START_INTERFACE_ChopReview
export interface ChopReview {
  id: string;
  author_name: string;
  author_role: string | null;
  rating: number;
  content: string;
  created_at: string;
  status?: string;
  owner_reply?: string | null;
}
// # END_INTERFACE_ChopReview

// # START_INTERFACE_ReviewManagerProps
interface ReviewManagerProps {
  /** ID компании */
  companyId: string;
  /** Список отзывов */
  reviews: ChopReview[];
  /** Функция ответа на отзыв */
  onRespond: (reviewId: string, response: string) => Promise<{ success: boolean; error?: string }>;
}
// # END_INTERFACE_ReviewManagerProps

// # START_FUNCTION_getRoleLabel
/** Метка роли на русском */
function getRoleLabel(role: string | null): string {
  const map: Record<string, string> = {
    employee: "Сотрудник",
    client: "Клиент",
    partner: "Партнёр",
    anonymous: "Аноним",
  };
  return role ? (map[role] ?? "Аноним") : "Аноним";
}
// # END_FUNCTION_getRoleLabel

// # START_FUNCTION_getRoleIcon
function getRoleIcon(role: string | null) {
  if (role === "employee") return <User className="w-3 h-3" />;
  if (role === "client") return <Briefcase className="w-3 h-3" />;
  if (role === "partner") return <ThumbsUp className="w-3 h-3" />;
  return <User className="w-3 h-3" />;
}
// # END_FUNCTION_getRoleIcon

// # START_FUNCTION_getStatusBadge
/** Бейдж статуса отзыва */
function getStatusBadge(status: string | undefined) {
  switch (status) {
    case "approved":
      return (
        <Badge variant="success" className="flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Одобрен
        </Badge>
      );
    case "rejected":
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          Отклонён
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="flex items-center gap-1 text-[hsl(var(--portal-warn))] border-[hsl(var(--portal-warn))]/30">
          <Clock className="w-3 h-3" />
          На модерации
        </Badge>
      );
  }
}
// # END_FUNCTION_getStatusBadge

// # START_FUNCTION_StarRatingReadonly
/** Отображение звёздного рейтинга (только для чтения) */
function StarRatingReadonly({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={cn(
            "w-4 h-4",
            star <= rating
              ? "fill-[hsl(var(--portal-warn))] text-[hsl(var(--portal-warn))]"
              : "fill-transparent text-muted-foreground/30"
          )}
        />
      ))}
    </div>
  );
}
// # END_FUNCTION_StarRatingReadonly

// # START_FUNCTION_ReviewCard
/** Карточка одного отзыва с возможностью ответа */
function ReviewCard({
  review,
  onRespond,
}: {
  review: ChopReview;
  onRespond: (reviewId: string, response: string) => Promise<{ success: boolean; error?: string }>;
}) {
  const { toast } = useToast();
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;

    setSending(true);
    try {
      const result = await onRespond(review.id, replyText.trim());
      if (result.success) {
        toast({
          title: "Ответ отправлен",
          description: "Ваш ответ появится под отзывом.",
        });
        setReplyOpen(false);
        setReplyText("");
      } else {
        toast({
          variant: "destructive",
          title: "Ошибка",
          description: result.error ?? "Не удалось отправить ответ.",
        });
      }
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Ошибка",
        description: e?.message ?? "Произошла непредвиденная ошибка.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border rounded-none p-4 space-y-3">
      {/* Шапка: автор, роль, рейтинг, статус */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="font-semibold text-sm">{review.author_name}</div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            {getRoleIcon(review.author_role)}
            {getRoleLabel(review.author_role)}
            <span className="mx-1">·</span>
            {new Date(review.created_at).toLocaleDateString("ru-RU")}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StarRatingReadonly rating={review.rating} />
          {getStatusBadge(review.status)}
        </div>
      </div>

      {/* Текст отзыва */}
      <p className="text-sm text-foreground/80 leading-relaxed">{review.content}</p>

      {/* Ответ владельца (если есть) */}
      {review.owner_reply && (
        <div className="ml-4 pl-3 border-l-2 border-primary/30 bg-muted/30 rounded-none p-3">
          <p className="text-xs font-semibold text-primary mb-1">Ответ владельца:</p>
          <p className="text-sm text-foreground/70">{review.owner_reply}</p>
        </div>
      )}

      {/* Кнопка ответа / инлайн-форма */}
      {replyOpen ? (
        <div className="space-y-2 pt-1">
          <Textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Напишите ваш ответ..."
            rows={3}
            maxLength={2000}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setReplyOpen(false);
                setReplyText("");
              }}
              disabled={sending}
            >
              Отмена
            </Button>
            <Button
              size="sm"
              onClick={handleSendReply}
              disabled={sending || !replyText.trim()}
              className="gap-1.5"
            >
              {sending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              {sending ? "Отправка..." : "Отправить"}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setReplyOpen(true)}
          className="gap-1.5 text-muted-foreground hover:text-foreground"
        >
          <Reply className="w-3.5 h-3.5" />
          Ответить
        </Button>
      )}
    </div>
  );
}
// # END_FUNCTION_ReviewCard

// # START_FUNCTION_ReviewManager
export function ReviewManager({
  companyId,
  reviews,
  onRespond,
}: ReviewManagerProps) {
  // Фильтрация: сначала одобренные, потом на модерации, потом отклонённые
  const sortedReviews = [...reviews].sort((a, b) => {
    const statusOrder: Record<string, number> = { approved: 0, pending: 1, rejected: 2 };
    const aOrder = statusOrder[a.status ?? "pending"] ?? 1;
    const bOrder = statusOrder[b.status ?? "pending"] ?? 1;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const approvedCount = reviews.filter((r) => r.status === "approved").length;
  const pendingCount = reviews.filter((r) => !r.status || r.status === "pending").length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageSquare className="w-4 h-4 text-muted-foreground" />
              Отзывы о компании
            </CardTitle>
            <CardDescription>
              Управление отзывами и ответы пользователям
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1 font-mono tabular-nums">
              <CheckCircle className="w-3.5 h-3.5 text-[hsl(var(--portal-accent))]" />
              {approvedCount}
            </span>
            {pendingCount > 0 && (
              <span className="flex items-center gap-1 font-mono tabular-nums">
                <Clock className="w-3.5 h-3.5 text-[hsl(var(--portal-warn))]" />
                {pendingCount}
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {reviews.length === 0 ? (
          /* Пустое состояние */
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
              <MessageSquare className="w-7 h-7 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-foreground mb-1">
              Пока нет отзывов
            </h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Когда пользователи начнут оставлять отзывы, они появятся здесь.
              Вы сможете отвечать на них и управлять модерацией.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedReviews.map((review) => (
              <ReviewCard
                key={review.id}
                review={review}
                onRespond={onRespond}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
// # END_FUNCTION_ReviewManager
